import {
  AgxCaption,
  AgxColumn,
  AgxHeader,
  AgxRow,
  QldContractStage,
  NswContractStage,
} from '@urbanx/agx-ui-components';
import { useAppDispatch } from 'hooks/useAppDispatch';
import { selectContract } from '../../../../../../campaigns/campaignsReducer';
import { formatCurrencyWithConditionalDecimals } from 'utils/formatNumber';
import { getMomentFromNow } from 'utils/utcDateFromNow';
import './ContractCard.scss';
import clsx from 'clsx';
import { getEnumValue } from 'helpers/enumHelper';
import {
  CampaignDetailModelState,
  ContractDetailModel,
  InProgressFormModelFormType,
} from '@urbanx/agx.campaigns.client';

interface ContractCardProps {
  state: CampaignDetailModelState;
  contract: ContractDetailModel & {
    formType?: InProgressFormModelFormType | string;
  };
  propertyImage: string;
  isArchived: boolean;
}

const inProgressFormDetails: Partial<
  Record<
    InProgressFormModelFormType,
    {
      label: string;
      title: string;
    }
  >
> = {
  [InProgressFormModelFormType.ContractExchange]: {
    label: 'Draft Contract',
    title: 'Exchange in progress',
  },
  [InProgressFormModelFormType.ContractRequest]: {
    label: 'Sales Advice',
    title: 'Sales advice in progress',
  },
  [InProgressFormModelFormType.ContractSubmission]: {
    label: 'Contract Submission',
    title: 'Submission in progress',
  },
};

const nonSelectableContractStages: Partial<Record<CampaignDetailModelState, string[]>> = {
  [CampaignDetailModelState.QLD]: [
    QldContractStage.PendingCompliance,
    QldContractStage.BackUp,
    QldContractStage.Accepted,
    QldContractStage.Unconditional,
    QldContractStage.Settled,
    QldContractStage.Terminated,
  ],
  [CampaignDetailModelState.NSW]: [
    NswContractStage.ContractRequested,
    NswContractStage.PendingCompliance,
    NswContractStage.Accepted,
    NswContractStage.Unconditional,
    NswContractStage.Settled,
    NswContractStage.Terminated,
    NswContractStage.Cancelled,
  ],
};

const ContractCard = ({ state, contract, propertyImage, isArchived }: ContractCardProps) => {
  const dispatch = useAppDispatch();
  const { lastUpdate, buyers = [], salePrice, contractId, contractStage } = contract;

  const formType = contract.formType ?? contract.inProgressForm?.formType;
  const inProgressFormDetail = inProgressFormDetails[formType as InProgressFormModelFormType];

  const buyerNames =
    buyers.length === 0
      ? (inProgressFormDetail?.title ?? 'No Name')
      : buyers
          .map((buyer) => {
            if (buyer.individual)
              return `${buyer.individual.name?.firstName} ${buyer.individual.name?.lastName}`;
            if (buyer.company) return buyer.company.companyName;
            if (buyer.powerOfAttorney)
              return `${buyer.powerOfAttorney.principalName?.firstName} ${buyer.powerOfAttorney.principalName?.lastName}`;

            return '';
          })
          .join(', ');

  const imageClasses = clsx('propertyImage', isArchived && 'archived');

  const stageTypes: { [key: string]: object } = {
    [CampaignDetailModelState.NSW]: NswContractStage,
    [CampaignDetailModelState.QLD]: QldContractStage,
  };

  const contractStageLabel = contractStage
    ? (getEnumValue(stageTypes[state], contractStage) ?? contractStage)
    : undefined;
  const stageLabel =
    state === CampaignDetailModelState.NSW && contractStageLabel === NswContractStage.ContractDraft
      ? 'Draft Contract'
      : (contractStageLabel ?? inProgressFormDetail?.label);
  const isSelectable =
    stageLabel == null || !nonSelectableContractStages[state]?.includes(stageLabel);
  const contractCardClasses = clsx('contractCard', !isSelectable && 'notClickable');

  return (
    <AgxRow
      fill
      extraClasses={contractCardClasses}
      justifyCenter
      largeGap
      centered
      onClick={isSelectable ? () => dispatch(selectContract(contractId ?? null)) : undefined}
    >
      <img className={imageClasses} src={propertyImage} alt="Property" />
      <AgxColumn fill>
        <AgxRow fill spaceBetween centered>
          <AgxCaption small>{stageLabel}</AgxCaption>
          <AgxCaption>{lastUpdate ? getMomentFromNow(lastUpdate) : ''}</AgxCaption>
        </AgxRow>
        <AgxHeader size={4} extraClasses="buyerNames">
          {buyerNames}
        </AgxHeader>
        <AgxHeader size={4}>
          {salePrice == null ? `$-- --` : formatCurrencyWithConditionalDecimals(salePrice, 2)}
        </AgxHeader>
      </AgxColumn>
    </AgxRow>
  );
};

export default ContractCard;
